/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { ComponentLike } from '@glint/template';
import DomainModel, { IPPoolOptions } from 'cio/models/domain';
import ApiErrorsService from 'cio/services/api-errors';
import FeaturesService from 'ember-feature-flags/services/features';
import { next } from '@ember/runloop';
import DomainMailServersSharedRequestForm from 'cio/components/domain/mail-servers/shared-request-form';
import DomainMailServersTransactionalRequestForm from 'cio/components/domain/mail-servers/transactional-request-form';
import DomainMailServersDedicatedRequestForm from 'cio/components/domain/mail-servers/dedicated-request-form';

interface DomainMailServersArgs {
  domain: DomainModel;
  hasCustomMailServers: boolean;
}

type MailServerOption = {
  pendingKey: IPPoolOptions;
  activeKey: IPPoolOptions;
  title: string;
  description: string;
  requestFormComponent: ComponentLike<any>;
};

const getMailServerOptions: (domainName: string) => Readonly<MailServerOption[]> = (domainName) =>
  Object.freeze([
    {
      pendingKey: 'shared_requested',
      activeKey: 'shared',
      title: 'Shared IP Pool - Default',
      description: `By default, emails sent from the ${domainName} domain will be sent over a shared IP pool. Reach out to our deliverability team to send emails through a dedicated IP.`,
      requestFormComponent: DomainMailServersSharedRequestForm,
    },
    {
      activeKey: 'transactional',
      pendingKey: 'transactional_requested',
      title: 'Shared IP Pool - Transactional',
      description: `Use the transactional pool to gain deliverability benefits for the Transactional API. Once enabled, you will no longer able to use ${domainName} from addresses in newsletters or campaigns.`,
      requestFormComponent: DomainMailServersTransactionalRequestForm,
    },
    {
      activeKey: 'dedicated',
      pendingKey: 'dedicated_requested',
      title: 'Dedicated IPs',
      description: 'For high volume senders, send emails over dedicated IPs to take control over deliverability.',
      requestFormComponent: DomainMailServersDedicatedRequestForm,
    },
  ]);

export default class DomainMailServers extends Component<DomainMailServersArgs> {
  @service apiErrors!: ApiErrorsService;
  @service features!: FeaturesService;

  @tracked selectedPool?: MailServerOption;

  constructor(owner: unknown, args: DomainMailServersArgs) {
    super(owner, args);

    if (!this.args.hasCustomMailServers) {
      next(() => {
        this.selectedPool = this.currentDomainPool;
      });
    }
  }

  get mailServerOptions(): (MailServerOption & {
    description: string;
    isSelected: boolean;
    isEnabled: boolean;
    isPending: boolean;
    isDisabled: boolean;
  })[] {
    let { domain } = this.args;

    return getMailServerOptions(domain.domain).map((option) => {
      let isEnabled = domain.ipPool && domain.ipPool === option.activeKey;
      let isPending = domain.ipPool && domain.ipPool === option.pendingKey;
      let isSelected = this.selectedPool && option.activeKey === this.selectedPool.activeKey ? true : false;

      return {
        ...option,
        isEnabled,
        isPending,
        isSelected,
        isDisabled: this.args.hasCustomMailServers,
      };
    });
  }

  get currentDomainPool() {
    return this.mailServerOptions.find((option) =>
      [option.activeKey, option.pendingKey].includes(this.args.domain.ipPool),
    );
  }

  requestPoolTask = dropTask(async (data: { volume?: string; comments?: string } = {}) => {
    return this.args.domain.requestIpPool({ ip_pool: this.selectedPool?.activeKey, ...data });
  });

  @action
  selectPool(pool: MailServerOption) {
    this.selectedPool = pool;
  }
}
